import { FC, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Arrow } from '$icons/uncolored/ArrowWithTail.svg';
import SPKCandidateCardSkill from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateSkills';
import SPKCandidateHiddenSkills from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateHiddenSkills';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { useAppSelector } from '$hooks/use-app-selector.hook';

import StatusCandidatePosition from '$types/serverTypes/StatusCandidatePosition';
import PositionDto from '../../types/serverTypes/positionDto';
import classes from './PositionPage.module.css';
import PositionCardMenu from './PositionCardMenu/PositionCardMenu';
import SPKJobModal, { SPKJobModalActionsTypes } from './SPKJobModal';

interface Props {
  position: PositionInternalDto | PositionDto;
}

const PositionCard: FC<Props> = ({ position }) => {
  const { currencyCode, currency, usdToCurrencyConverter } = useAppSelector(
    (state) => state.brand
  );
  const [t] = useTranslation('global');
  const [openModal, setOpenModal] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const card = useRef<HTMLDivElement>(null);

  const [overflowStart, setOverflowStart] = useState<number>();
  const [hiddenSkills, setHiddenSkills] = useState<
    PositionDto['Skills_position']
  >([]);
  const [visibleSkills, setVisibleSkills] = useState<
    PositionDto['Skills_position']
  >([]);

  const skillsContainerRef = useRef<HTMLDivElement | null>(null);
  const candidatesCount =
    position.CandidatesPositionStatus?.filter(
      (singleCandidate) =>
        singleCandidate.Status !== StatusCandidatePosition.declined &&
        singleCandidate.Status !== StatusCandidatePosition.interested
    )?.length || 0;

  const onOverflowHandler = (index: number) => {
    setOverflowStart(index);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsDuplicating(false);
  };

  useEffect(() => {
    if (!position.Skills_position) return;

    if (!overflowStart && position.Skills_position) {
      setVisibleSkills(position.Skills_position);
      return;
    }

    const visible = position.Skills_position.slice(0, overflowStart);
    const hidden = position.Skills_position.slice(overflowStart);

    setVisibleSkills(visible);
    setHiddenSkills(hidden);
  }, [position.Skills_position, overflowStart]);

  return (
    <>
      <div>
        <div
          ref={card}
          className="rounded-t-xl bg-white p-4"
          data-testid="positionCard"
          tabIndex={-1}
        >
          <div className={`mb-4 ${classes.cardHeader}`}>
            <div className={classes.cardJob}>
              <p className="text-xl font-bold">
                {position.CustomJobTitle || position.JobPosition.title}
              </p>
            </div>
            <PositionCardMenu
              isInternal={false}
              setOpenEditModal={setOpenModal}
              position={position}
              handleOpenDuplicateModal={() => {
                setIsDuplicating(true);
                setOpenModal(true);
              }}
            />
          </div>
          <div className="flex flex-row divide-x -mx-2 *:px-2 text-sm">
            <div className="flex flex-row gap-2">
              <span className="text-spk-mediumContrast">{`${t(
                'PositionCard.Experience'
              )}`}</span>
              <span>{`${position.ExperienceYears} years`}</span>
            </div>

            <div className="flex flex-row gap-2">
              <span className="text-spk-mediumContrast">
                {t('PositionCard.MonthlyBudget', { currencyCode })}:
              </span>
              <span>{`${currency}${usdToCurrencyConverter(
                position.SalaryLowerBound
              )} - ${currency}${usdToCurrencyConverter(
                position.SalaryUpperBound
              )}`}</span>
            </div>
          </div>

          {/* Skills */}
          <div className="items-center text-sm flex flex-row gap-2 mb-4 mt-2 text-nowrap text-spk-mediumContrast">
            <p>{`${t('PositionCard.SkillsetOverview')}:`}</p>
            <div className="flex flex-row gap-2 overflow-hidden">
              {visibleSkills &&
                visibleSkills
                  .slice(0, overflowStart)
                  .map((singleSkill, index) => {
                    return (
                      <SPKCandidateCardSkill
                        key={`${singleSkill.Title}_${singleSkill.Id}`}
                        skill={singleSkill.Title}
                        index={index}
                        containerRef={
                          skillsContainerRef.current as HTMLDivElement
                        }
                        setOverflowIndex={onOverflowHandler}
                      />
                    );
                  })}
            </div>

            {hiddenSkills && hiddenSkills.length > 0 && (
              <SPKCandidateHiddenSkills
                hiddenSkills={hiddenSkills.map((singleSkill) => ({
                  Id: singleSkill.Id,
                  Skill: { title: singleSkill.Title },
                }))}
                uniqueId={position.Id}
              />
            )}
          </div>
        </div>
        <Link
          data-testid="viewCandidatesButton"
          role="button"
          tabIndex={0}
          className="text-sm font-bold text-employer-primary flex flex-row justify-end items-center gap-2 bg-spk-neutral-10 rounded-b-xl p-4"
          onKeyDown={() => {}}
          to={`/employer-dashboard/positions/${position.Id}`}
        >
          {`${candidatesCount} ${t('PositionCard.candidates')}`}
          <Arrow className="fill-employer-primary" />
        </Link>
      </div>
      <ModalInTailwind isOpen={openModal} closeModal={handleCloseModal}>
        <SPKJobModal
          position={position}
          closeHandler={handleCloseModal}
          action={
            isDuplicating
              ? SPKJobModalActionsTypes.duplicate
              : SPKJobModalActionsTypes.edit
          }
        />
      </ModalInTailwind>
    </>
  );
};

export default PositionCard;
